import { Css } from "~generated/css";
import { currencyFormatter } from "~utils";
import {
  SummaryPrintConfiguration_CheckoutConfigFragment,
  SummaryPrintConfiguration_EstimatedInvoiceScheduleItemFragment,
} from "../SummaryPrint.page.generated";
import { PDFPage } from "./PDFPage";
import { PrintHeader } from "./PrintHeader";

type EstimatedInvoiceScheduleReportProps = {
  data: SummaryPrintConfiguration_CheckoutConfigFragment;
  shouldDisplayPricing: boolean;
  mhEstimatedInvoiceSchedule?: SummaryPrintConfiguration_EstimatedInvoiceScheduleItemFragment[];
  aduEstimatedInvoiceSchedule?: SummaryPrintConfiguration_EstimatedInvoiceScheduleItemFragment[];
  invoiceCategoryNames?: Record<string, string>;
};

export function EstimatedInvoiceScheduleReport({
  data,
  mhEstimatedInvoiceSchedule,
  aduEstimatedInvoiceSchedule = [],
  invoiceCategoryNames = {},
}: EstimatedInvoiceScheduleReportProps) {
  const { name, status, createdAt, reservedAt, expiresAt, customerEmail } = data;
  const hasMh = mhEstimatedInvoiceSchedule?.nonEmpty ?? false;
  const hasAdu = aduEstimatedInvoiceSchedule.nonEmpty;
  if (!hasMh && !hasAdu) return null;
  const mhTotalInCents = (mhEstimatedInvoiceSchedule ?? []).reduce((sum, item) => sum + item.invoiceAmountInCents, 0);
  const aduTotalInCents = aduEstimatedInvoiceSchedule.reduce((sum, item) => sum + item.invoiceAmountInCents, 0);
  const grandTotalInCents = mhTotalInCents + aduTotalInCents;

  return (
    <PDFPage>
      <div css={Css.relative.h100.w100.bgWhite.$}>
        <div css={Css.df.ma.fdc.gap2.p1.pb4.asc.maxContainer.$}>
          <PrintHeader
            status={status}
            expiredAt={expiresAt}
            createdAt={createdAt}
            reservedAt={reservedAt}
            configurationName={name}
            customerEmail={customerEmail}
          />
          <h2 css={Css.t18.fw500.mb1.$}>Estimated Invoice Schedule</h2>
          {/* Table header */}
          <div css={Css.df.w100.jcsb.pb1.add("borderBottom", "2px solid").add("borderBottomColor", "#374151").$}>
            <span css={Css.t14.fw500.gray800.$}>Category</span>
            <span css={Css.t14.fw500.gray800.$}>Amount</span>
          </div>
          {/* Main House section */}
          {hasMh && (
            <ScheduleSection
              label={hasAdu ? "Main House" : undefined}
              items={mhEstimatedInvoiceSchedule!}
              totalInCents={mhTotalInCents}
              invoiceCategoryNames={invoiceCategoryNames}
              showSubtotal={hasAdu}
            />
          )}
          {/* ADU section */}
          {hasAdu && (
            <ScheduleSection
              label="ADU"
              items={aduEstimatedInvoiceSchedule}
              totalInCents={aduTotalInCents}
              invoiceCategoryNames={invoiceCategoryNames}
              showSubtotal={hasMh}
            />
          )}
          {/* Total row */}
          <div css={Css.df.w100.jcsb.ptPx(12).$}>
            <span css={Css.t14.fw500.gray800.$}>Total</span>
            <span css={Css.t14.fw500.gray800.$}>{currencyFormatter(grandTotalInCents)}</span>
          </div>
          {/* Disclaimer */}
          <div css={Css.t12.pt2.tac.gray700.$}>
            <sup>*</sup>Estimated invoice schedule is subject to change. Final amounts will be confirmed prior to
            contract execution.
          </div>
        </div>
      </div>
    </PDFPage>
  );
}

type ScheduleSectionProps = {
  label?: string;
  items: SummaryPrintConfiguration_EstimatedInvoiceScheduleItemFragment[];
  totalInCents: number;
  invoiceCategoryNames: Record<string, string>;
  showSubtotal: boolean;
};

function ScheduleSection({ label, items, totalInCents, invoiceCategoryNames, showSubtotal }: ScheduleSectionProps) {
  return (
    <>
      {label && (
        <div css={Css.df.w100.jcsb.ptPx(16).pbPx(4).$}>
          <span css={Css.t14.fw600.gray800.$}>{label}</span>
          <span css={Css.t14.fw600.gray800.$}>{currencyFormatter(totalInCents)}</span>
        </div>
      )}
      {items.map((item, i) => (
        <div
          key={item.costCodeInvoiceCategory}
          css={{
            ...Css.df.w100.jcsb.py1
              .if(showSubtotal && i === items.length - 1)
              .pb0.else.add("borderBottom", "1px dotted")
              .add("borderBottomColor", "#d1d5db").$,
          }}
        >
          <span css={Css.t14.gray700.$}>
            {invoiceCategoryNames[item.costCodeInvoiceCategory] ?? item.costCodeInvoiceCategory}
          </span>
          <span css={Css.t14.gray700.$}>{currencyFormatter(item.invoiceAmountInCents)}</span>
        </div>
      ))}
      {showSubtotal && (
        <div css={Css.df.w100.jcsb.ptPx(12).pbPx(4).add("borderTop", "1px solid").add("borderTopColor", "#d1d5db").$}>
          <span css={Css.t14.fw500.gray700.$}>Subtotal</span>
          <span css={Css.t14.fw500.gray700.$}>{currencyFormatter(totalInCents)}</span>
        </div>
      )}
    </>
  );
}
